import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormSwitch } from '../FormInputs';
import { ReactComponent as AIIcon } from '../../assets/ai.svg';
import type { NarrativeFormData } from '../Forms/types';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { SentimentAnalysisSubgroup } from './SentimentAnalysisSubgroup';
import { StoriesSubgroup } from './StoriesSubgroup';
import { TopicModelingSubgroup } from './TopicModelingSubgroup';

interface AIAnalysisGroupProps {
  form: any;
  narrativeTypes: any[];
  isDisabled?: boolean;
}

export const AIAnalysisGroup: React.FC<AIAnalysisGroupProps> = ({ form, narrativeTypes, isDisabled = false }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const aspectBasedSentimentAvailable = featureFlags?.includes('aspect_based_sentiment');
  const showStories = featureFlags?.includes('show_stories');

  return (
    <form.Subscribe
      selector={(state: any) => state.values.currentNarrativeType}
      children={(currentNarrativeType: any) => {
        const currentNarrativeTypeData = narrativeTypes[currentNarrativeType || 0];
        
        const showTopicsSection = 
          (currentNarrativeTypeData?.type !== 'TOPIC_MODELING' && showStories) || 
          currentNarrativeTypeData?.type === 'TOPIC_MODELING';
        
        const showAIAnalysisSection = aspectBasedSentimentAvailable || showTopicsSection;

        if (!showAIAnalysisSection) {
          return null;
        }

        return (
    <div className="form-wrapper">
      <div className="form-wrapper-header with-margin">
        <h2>{t('AI Analysis')}</h2>
      </div>

      {aspectBasedSentimentAvailable && (
        <>
          <div className="form-group-header">
            <span>{t('Sentiment analysis')}</span>
          </div>
          <div className="group-card">
            <div className="group-card-row">
              <FormSwitch
                form={form}
                name="isAspectBasedSentimentEnabled"
                label={
                  <div>
                    <span className="label-icon">
                      <AIIcon />
                    </span>
                    {t('Contextual sentiment')}
                  </div>
                }
                subLabel={t('Detect contextual sentiments toward specific people, countries, events and more.')}
                disabled={isDisabled}
              />

              <form.Field
                name="isAspectBasedSentimentEnabled"
                children={(field: any) => (
                  field.state.value && (
                    <SentimentAnalysisSubgroup form={form} isDisabled={isDisabled} />
                  )
                )}
              />
            </div>
          </div>
        </>
      )}

      {showTopicsSection && (
        <div>
          <div className="form-group-header">
            <span>{t('Publication grouping')}</span>
          </div>

          <div className="group-card">
            {currentNarrativeTypeData?.type !== 'TOPIC_MODELING' && showStories && (
              <div className="group-card-row">
                <FormSwitch
                  form={form}
                  name="createStories"
                  label={
                    <div>
                      <span className="label-icon">
                        <AIIcon />
                      </span>
                      {t('Stories')}
                    </div>
                  }
                  subLabel={t('Group publications into key topics based on semantic similarity.')}
                  disabled={isDisabled}
                />

                <form.Field
                  name="createStories"
                  children={(field: any) => (
                    field.state.value && (
                      <StoriesSubgroup form={form} />
                    )
                  )}
                />
              </div>
            )}

            {currentNarrativeTypeData?.type === 'TOPIC_MODELING' && (
              <TopicModelingSubgroup form={form} isDisabled={isDisabled} />
            )}
          </div>
        </div>
      )}
    </div>
        );
      }}
    />
  );
};